import "../vr.css";

import { AppContext } from "@Context/common/general/context";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import FallbackNavigate from "@Helpers/fallback-navigate";
import makeRoute from "@Helpers/make-route";
import Storage from "@Storage";
import { ThemeProvider as ThemeProviderV5 } from "@mui/material/styles";
import React, { lazy, useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";

// Containers
import SignIn from "@Containers/auth/sign-in";
import Coach from "@Containers/game/coach";
import MatchPlay from "@Containers/game/match-play";
import Practise from "@Containers/game/practise";

const SuperAdmin = lazy(() => import("@Containers/admin/admin"));
const ClubsAdmin = lazy(() => import("@Containers/admin/clubs-admin"));
const PlayersAdmin = lazy(() => import("@Containers/admin/players-admin"));
const TeamsAdmin = lazy(() => import("@Containers/admin/teams-admin"));
const ChallengesAdmin = lazy(() => import("@Containers/admin/challenges/challenges-admin"));
const AccountsAdmin = lazy(() => import("@Containers/admin/accounts/accounts-admin"));
const ChallengeEditor = lazy(() => import("@Containers/admin/challenges/challenge-editor/challenge-editor"));
const RewardEditor = lazy(() => import("@Containers/admin/challenges/reward-editor"));

// Components
import PreloadImages from "@Components/preload-images";
import Error from "@Services/error";
import Loader from "@Services/loader";

// Services
// import Results from "@Containers/game/results";
import { HealthAndSafetyService } from "@Services/health-and-safety/health-and-safety-service";
import NavigateService from "@Services/navigation-service";
import NotificationService from "@Services/notification-service/notification-service";
import TooltipService from "@Services/tool-tip-service";
import WizardService from "@Services/wizard-service";

// Platform Services
// import CreateAccount from "@Containers/app/sign-up/create-account";
import AuthService from "@Services/auth-service";
import DevService from "@Services/dev-service";

import { BootcampHeader, Header, StaticBackground } from "@Components/game";
import { InitializationProgress, VRNotificationDialog } from "@Components/game/common";
import AccountSelection from "@Containers/game/account/account-selection-vr";
import AddProfile from "@Containers/game/account/add-profile-vr";
import InviteUser from "@Containers/game/account/invite-user-vr";
import PinEntry from "@Containers/game/account/pin-entry-vr";
import ProfileSelection from "@Containers/game/account/profile-selection-vr";
import Bootcamp from "@Containers/game/bootcamp";
import { Builder } from "@Containers/game/builder/builder";
import Career from "@Containers/game/career";
import Category from "@Containers/game/category";
import Challenge from "@Containers/game/challenges/challenge";
import ChallengeSessions from "@Containers/game/challenges/challenge-sessions";
import Challenges from "@Containers/game/challenges/challenges";
import BybCatLauncher from "@Containers/game/coach/bybcat-launcher";
import BybCatResult from "@Containers/game/coach/bybcat-result";
import CustomPlaylists from "@Containers/game/coach/custom-playlists";
import ScenarioEditor from "@Containers/game/coach/scenario-editor";
import Guides from "@Containers/game/guides";
import HomeV2 from "@Containers/game/home";
import PlaylistSessions from "@Containers/game/playlists/playlist-sessions";
import Playlists from "@Containers/game/playlists/playlists";
import SessionResults from "@Containers/game/results/session-results";
// import AudioSettings from "@Containers/game/settings/audio-settings";
import ActivityPage from "@Containers/game/activity";
import Playlist from "@Containers/game/playlists/playlist";
import PlaylistCategory from "@Containers/game/playlists/playlist-categories";
import Progress from "@Containers/game/progress";
import AudioSettings from "@Containers/game/settings/audio-settings";
import ControllerSettings from "@Containers/game/settings/controller-settings";
import FeedbackSettings from "@Containers/game/settings/feedback-settings";
import KitEditor from "@Containers/game/settings/kit-editor";
import LanguageSettings from "@Containers/game/settings/language-settings";
import Members from "@Containers/game/settings/members";
import Profile from "@Containers/game/settings/profile";
import Settings from "@Containers/game/settings/settings";
import RoutesWrapper from "@Containers/shared/route-wrapper";
import Permissions from "@Context/common/permissions/permissions";
import { BuilderContextProvider } from "@Context/game/builder/builder-context";
import { TrainingPlansProgressProvider } from "@Context/game/training-plans/training-plans-progress-context";
import AccountAuthorizations from "@Enums/AccountAuthorizations";
import AccountRoles from "@Enums/AccountRoles";
import routeTracker from "@Helpers/route-tracker";
import AccountService from "@Services/account-service";
import AudioService from "@Services/audio-service";
import ClientVersionAnalyticsService from "@Services/client-version-analytics-service";
import DialogRegistryService from "@Services/dialog-registry-service";
import KeyboardService from "@Services/keyboard-service";
import OneSignalVRWebService from "@Services/onesignal-vr-web-service";
import PreferencesService from "@Services/preferences-service";
import TrainingPlansService from "@Services/training-plans-service";
import VideoService from "@Services/video-service";
import { getTheme } from "@Themes/vr-theme";
import useAuthorizations from "@hooks/common/general/use-authorizations";

function AppRouter() {
  const { state, dispatch } = React.useContext(AppContext);

  const [accountInfoResolved, setAccountInfoResolved] = useState(false);
  const [exlusiveMode, setExlusiveMode] = useState(false);
  const [isPassThrough, setIsPassThrough] = useState(false);

  const isAccountSelected = state.account.id;
  const { BYBCAT_ACCESS, EDITOR_ACCESS, CUSTOM_PLAYLISTS } = useAuthorizations([
    AccountAuthorizations.BYBCAT_ACCESS,
    AccountAuthorizations.EDITOR_ACCESS,
    AccountAuthorizations.CUSTOM_PLAYLISTS,
  ]);

  const isAuth = state.user.authenticated;
  const isSuperAdmin = state.user.isSuperAdmin;
  const isSubscribed = state.user.hasAccessSubscriptions;
  const hasVrAccess = isSubscribed || state.user.inBootcamp;
  const isExpired = state.user.hasAllAccessExpired;

  const onGameEntered = () => {
    setIsPassThrough(true);
  };

  const onGameExited = () => {
    setIsPassThrough(false);
  };

  const [isDev, setIsDev] = useState<boolean>(false);
  useEffect(() => {
    const devUrl: string = Storage.get({ key: "devStoredUrl" });
    if (devUrl) setIsDev(!devUrl.includes(window.location.hostname));

    // Handle exclusiveMode
    state.app.events.exclusivePageEntered.uniq(() => {
      setExlusiveMode(true);
    });
    state.app.events.exclusivePageExited.uniq(() => {
      setExlusiveMode(false);
    });

    // Handle game entered
    state.editor.events.editorEntered.uniq(onGameEntered);
    state.editor.events.editorExited.uniq(onGameExited);

    // Handle account resolved
    if (state.user.authenticated) {
      setAccountInfoResolved(true);
    } else {
      state.auth.events.accountInfoUpdated.uniq(() => {
        setAccountInfoResolved(true);
      });
    }
  }, []);

  const theme = getTheme("dark");
  theme.typography.htmlFontSize = 16; //TODO_16px is the default font size for the browser

  return isDev ? (
    <DevService />
  ) : (
    <ThemeProviderV5 theme={theme}>
      <Router>
        {/* Setup global components */}
        <StaticBackground />
        <HealthAndSafetyService />
        <NotificationService />
        <ClientVersionAnalyticsService />
        <PreloadImages />
        <NavigateService />
        <Error />
        <Loader />
        <WizardService />
        <AuthService />
        <TooltipService />
        <VideoService />
        <AudioService />
        <KeyboardService />
        <VRNotificationDialog />
        <AccountService />

        {isAuth && <DialogRegistryService />}
        {isAuth && <OneSignalVRWebService />}
        {isAuth && <PreferencesService />}

        <TrainingPlansProgressProvider>
          {isAuth && isSubscribed && <TrainingPlansService />}

          {/* App Bar VR */}
          {hasVrAccess && !exlusiveMode && accountInfoResolved && !isPassThrough && (state.user.inBootcamp ? <BootcampHeader /> : <Header />)}
        </TrainingPlansProgressProvider>

        <InitializationProgress initialized={state.app.initialized} />

        {state.app.initialized &&
          // Track the path & order that routes are defined, this is used for animations directions
          routeTracker(
            <RoutesWrapper>
              {/**
               * Not authenticated routes
               **/}
              {!isAuth && [{ path: "*", el: <SignIn /> }].map(makeRoute)}

              {/**
               * Account selection routes
               */}
              {isAuth && !isAccountSelected && [{ path: "/select-account", el: <AccountSelection /> }].map(makeRoute)}

              {/**
               * Subscribed only routes
               **/}
              {isAuth &&
                hasVrAccess &&
                !isExpired &&
                [
                  { path: "/", el: <FallbackNavigate path="/home" /> },
                  { path: "/select-account", el: <AccountSelection /> },
                  ...(isAccountSelected
                    ? [
                        { path: "/select-profile", el: <ProfileSelection /> },
                        { path: "/select-profile/pin-entry/:userId", el: <PinEntry /> },
                        { path: "/select-profile/add", el: <AddProfile /> },
                        { path: "/select-profile/invite", el: <InviteUser /> },
                      ]
                    : []),
                  { path: "/home", el: state.user.inBootcamp ? <Bootcamp /> : <HomeV2 /> },
                  { path: "/home/career", el: <Career /> },
                  { path: "/home/career/results/:sessionId", el: <SessionResults headerButtonType={VRHeaderButtonType.CLOSE} /> },
                  { path: "/home/practise", el: <Practise /> },
                  { path: "/home/practise/results/:sessionId", el: <SessionResults headerButtonType={VRHeaderButtonType.CLOSE} /> },
                  { path: "/home/guides", el: <Guides /> },
                  { path: "/home/guides/:guide", el: <Guides /> },
                  { path: "/home/matchplay", el: <MatchPlay /> },
                  { path: "/home/matchplay/results/:sessionId", el: <SessionResults headerButtonType={VRHeaderButtonType.CLOSE} /> },
                  { path: "/progress", el: <Progress /> },
                  { path: "/activity", el: <ActivityPage /> },
                  { path: "/activity/session/:sessionId", el: <SessionResults headerButtonType={VRHeaderButtonType.BACK} /> },
                  { path: "/challenges", el: <Challenges /> },
                  { path: "/challenges/category/:tag", el: <Category /> },
                  { path: "/challenges/challenge/:challengeId", el: <Challenge /> },
                  { path: "/challenges/challenge/:challengeId/sessions", el: <ChallengeSessions /> },
                  {
                    path: "/challenges/challenge/:challengeId/sessions/session/:sessionId",
                    el: <SessionResults headerButtonType={VRHeaderButtonType.CLOSE} />,
                  },
                  { path: "/coach", el: <Coach /> },
                  { path: "/coach/playlists", el: <CustomPlaylists /> },
                  { path: "/settings", el: <Settings /> },
                  { path: "/settings/profile", el: <Profile userId={state.user.dbId!} /> },
                  { path: "/settings/members", el: <Members /> },
                  { path: "/settings/members/:memberId", el: <Profile /> },
                  { path: "/settings/members/add", el: <AddProfile /> },
                  { path: "/settings/members/invite", el: <InviteUser /> },
                  {
                    path: "/settings/my-kit",
                    el: (
                      <Permissions editor={AccountRoles.GUEST} viewer={AccountRoles.GUEST}>
                        <KitEditor pageId="SETTINGS-MY-KIT" />
                      </Permissions>
                    ),
                  },
                  {
                    path: "/settings/default-kit",
                    el: (
                      <Permissions editor={AccountRoles.ADMIN} viewer={AccountRoles.GUEST}>
                        <KitEditor pageId="SETTINGS-DEFAULT-KIT" isAccount />
                      </Permissions>
                    ),
                  },
                  { path: "/settings/audio", el: <AudioSettings /> },
                  { path: "/settings/feedback", el: <FeedbackSettings /> },
                  { path: "/settings/controller", el: <ControllerSettings /> },
                  { path: "/settings/language", el: <LanguageSettings /> },

                  ...(CUSTOM_PLAYLISTS || EDITOR_ACCESS
                    ? [
                        { path: "/playlists", el: <Playlists /> },
                        { path: "/playlists/category/:tag", el: <PlaylistCategory /> },
                        { path: "/playlists/playlist/:playlistId", el: <Playlist /> },
                        { path: "/playlists/playlist/:playlistId/sessions", el: <PlaylistSessions /> },
                        {
                          path: "/playlists/playlist/:playlistId/sessions/session/:sessionId",
                          el: <SessionResults headerButtonType={VRHeaderButtonType.CLOSE} />,
                        },
                      ]
                    : []),

                  ...(BYBCAT_ACCESS
                    ? [
                        { path: "/coach/bybcat", el: <BybCatLauncher /> },
                        { path: "/coach/bybcat/result/:resultId", el: <BybCatResult /> },
                      ]
                    : []),

                  ...(isSuperAdmin
                    ? [
                        { path: "/settings/admin", el: <SuperAdmin /> },
                        { path: "/settings/admin/users", el: <PlayersAdmin /> },
                        { path: "/settings/admin/teams", el: <TeamsAdmin /> },
                        { path: "/settings/admin/clubs", el: <ClubsAdmin /> },
                        { path: "/settings/admin/accounts-admin", el: <AccountsAdmin /> },
                        { path: "/settings/admin/challenges-rewards", el: <ChallengesAdmin /> },
                        { path: "/settings/admin/challenges-rewards/challenge-editor", el: <ChallengeEditor /> },
                        { path: "/settings/admin/challenges-rewards/challenge-editor/:challengeId", el: <ChallengeEditor /> },
                        { path: "/settings/admin/challenges-rewards/reward-editor/:rewardId", el: <RewardEditor /> },
                      ]
                    : []),

                  ...(EDITOR_ACCESS || isSuperAdmin
                    ? [
                        { path: "/coach/360/scenario-editor", el: <ScenarioEditor /> },
                        {
                          path: "/coach/360/scenario-editor/results/:sessionId",
                          el: <SessionResults headerButtonType={VRHeaderButtonType.BACK} />,
                        },
                        {
                          path: "/coach/360/builder/",
                          el: (
                            <BuilderContextProvider>
                              <Builder />
                            </BuilderContextProvider>
                          ),
                        },
                      ]
                    : []),

                  { path: "*", el: <FallbackNavigate path="/home" /> },
                ].map(makeRoute)}
            </RoutesWrapper>,
            state.app.availableRoutes,
            dispatch
          )}
      </Router>
    </ThemeProviderV5>
  );
}

export default AppRouter;
